import type React from 'react';
import Button from 'app/shared/core/Button';
import IconContact from 'images/icons/contact-white.svg';
import { listingUtils_userRecentlyContactedListing } from 'app/shared/utils/listingUtils';
import { useYieldCallback } from '@zillow/yield-callback';
import { useSelector } from 'react-redux';
import { hdpContactButtonV2LayoutStyles, styledContactButtonV2Styles } from './ContactButtonV2.styles';
import type { ListingDetails } from 'app/types/listingDetails.type';
import type { RootReduxState } from 'app/shared/flux/reducers';

// Experiments
import constants from 'app/shared/constants/ExperimentConstants';

interface ContactButtonV2Props {
  listing: ListingDetails;
  onClick?: () => void;
  contactButtonOffsetTop?: number;
}

const ContactButtonV2: React.FC<ContactButtonV2Props> = ({ listing, onClick }) => {
  const hideListing = !listing.contact?.displayContactBox;
  const onClickCallback = useYieldCallback(() => {
    onClick?.();
  });

  const recentlyContacted =
    listing.userItemTypes && listing.userItemDates
      ? listingUtils_userRecentlyContactedListing(listing.userItemTypes as any, listing.userItemDates as any)
      : false;

  // A/B Test: CTA Copy Test
  const ctaExperiment = useSelector(
    (state: RootReduxState) => state.analytics.runningExperiments[constants.HOTPADS_WEB_CTA_COPY_TEST],
  );

  const labelMap = {
    buttonLabel: 'Check availability',
    gaLabel: 'defaultCTA',
  };

  if (listing.waitlisted) {
    labelMap.buttonLabel = 'Add me to the waitlist';
    labelMap.gaLabel = 'waitlistedCTA';
  } else if (listing.popularity && listing.popularity.summary.inquiries === 0) {
    labelMap.buttonLabel = 'Be the first to contact!';
    labelMap.gaLabel = 'firstToContactCTA';
  }
  if (recentlyContacted) {
    labelMap.buttonLabel = 'Contact again?';
    labelMap.gaLabel = 'contactAgainCTA';
  }

  // Apply CTA experiment overrides (only if not waitlisted or recently contacted)
  if (ctaExperiment && !ctaExperiment.isControl && !listing.waitlisted && !recentlyContacted) {
    if (ctaExperiment.treatment === 'variant1') {
      labelMap.buttonLabel = 'Check availability';
      labelMap.gaLabel = 'ctaTestVariant1CTA';
    } else if (ctaExperiment.treatment === 'variant2') {
      labelMap.buttonLabel = 'Send message now!';
      labelMap.gaLabel = 'ctaTestVariant2CTA';
    }
    // If experiment exists but treatment doesn't match, fall back to control behavior
  }

  if (
    !listing ||
    hideListing ||
    !listing.active ||
    !listing.userItemDates ||
    !listing.userItemTypes ||
    listing.userOnWaitlist
  ) {
    return null;
  }

  return (
    <div className={hdpContactButtonV2LayoutStyles} id="ContactButtonV2Sticky" data-testid="contact-button-v2">
      <Button
        aria-label={labelMap.buttonLabel}
        className={`ContactButtonV2 ${styledContactButtonV2Styles}`}
        data-contact-label={labelMap.gaLabel}
        data-testid="contact-button-v2-button"
        btnType={'primary'}
        full
        bold
        size={'lg'}
        icon={{ src: IconContact, width: '22px', height: '20px', alt: '' }}
        onClick={onClickCallback}
      >
        {labelMap.buttonLabel}
      </Button>
    </div>
  );
};

export default ContactButtonV2;
